import { Access } from '@acies/domain';
import { AccessJSONSchema, SessionAccessJSONSchema } from '@acies/schema';
import { SessionStorageService } from '@shared/services/storage.service';
import { UserService } from '@shared/services/user.service';
import { Observable, of, switchMap } from 'rxjs';
import { accessTransform, sessionAccessTransform } from './adapters';

export default class AccessService {
	#sessionStorageService: SessionStorageService;
	#userService: UserService;
	public constructor(sessionStorageService: SessionStorageService, userService: UserService) {
		this.#sessionStorageService = sessionStorageService;
		this.#userService = userService;
	}

	public loadAccess(): Observable<Access | null> {
		const sessionAccess = this.#getAccessFromSessionStorage();
		if (sessionAccess) {
			return of(sessionAccess);
		}
		return this.#loadAccessFromAPI();
	}

	/**
	 * Tries to load the IUserAccessResult response with existing UserService.
	 *
	 * The response is safely validated and a new Access instance is returned.
	 */
	#loadAccessFromAPI(): Observable<Access | null> {
		return this.#userService.fetchAccountsObs().pipe(
			switchMap((value) => {
				const safe = AccessJSONSchema.safeParse(value);
				if (safe.success) {
					return of(accessTransform(safe.data));
				}
				return of(null);
			}),
		);
	}

	/**
	 * Tries to get the Access string data from Session Storage.
	 *
	 * We don't use safeParse here, since we want the try block to fail if JSON.parse or SessionAccessJSONSchema.parse throws an Error.
	 */
	#getAccessFromSessionStorage(): Access | null {
		try {
			const userAccessMapString = this.#sessionStorageService.get('User.AccessMap');
			const userAccessMap = JSON.parse(userAccessMapString);
			const data = SessionAccessJSONSchema.parse(userAccessMap);
			return sessionAccessTransform(data);
		} catch (error: unknown) {
			// eslint-disable-next-line unused-imports/no-unused-vars
			return null;
		}
	}
}
